import { Box, Typography } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { prop, uniqBy } from 'ramda'
import { ReactNode, useMemo } from 'react'
import { Unless, When } from 'react-if'

import { HistoryVersionModal } from '@/components'
import { VailHistory } from '@/utils/api'
import { formatDate } from '@/utils/dateUtils'
import { logError } from '@/utils/logger'

export type HistoryProps = {
  data?: VailHistory[]
  restore: (data: VailHistory) => void
  publishDialog?: ReactNode
  extraColumns?: GridColDef[]
  hideColumns?: Record<
    'valid' | 'name' | 'Created At' | 'Created By' | 'Notes' | 'View',
    boolean
  >
}

export const HistoricList = ({
  restore,
  data,
  publishDialog,
  extraColumns,
  hideColumns,
}: HistoryProps) => {
  const columns: GridColDef<VailHistory>[] = useMemo(() => {
    const appendColumn = (
      column: GridColDef<VailHistory>,
      hidden?: boolean
    ) => {
      if (hidden) return []
      return [column]
    }

    return uniqBy(prop('field'), [
      ...appendColumn(
        {
          field: 'Name',
          flex: 1,
          headerName: 'Name',
          valueGetter: (_, { name }) => name ?? '',
        },
        hideColumns?.name
      ),
      ...appendColumn(
        {
          field: 'Created At',
          flex: 1,
          headerName: 'Created At',
          valueGetter: (_, { createdAt }) => formatDate(createdAt),
        },
        hideColumns?.['Created At']
      ),
      ...appendColumn(
        {
          field: 'Created By',
          flex: 1,
          headerName: 'Created By',
          valueGetter: (_, { createdBy }) => createdBy?.name ?? '',
        },
        hideColumns?.['Created By']
      ),
      ...appendColumn(
        {
          field: 'Notes',
          flex: 1,
          headerName: 'Notes',
          valueGetter: (_, { notes }) => notes ?? '',
        },
        hideColumns?.Notes
      ),
      ...appendColumn(
        {
          field: 'View',
          flex: 1,
          headerName: 'View',
          renderCell: ({ row }) => {
            return (
              <HistoryVersionModal
                data={row.data}
                name={row.name ?? ''}
                notes={row.notes ?? ''}
                restoreVersion={() => {
                  try {
                    const parsedData = JSON.parse(row.data)
                    restore(parsedData)
                  } catch (error) {
                    logError(error)
                  }
                }}
              />
            )
          },
        },
        hideColumns?.View
      ),
      ...appendColumn(
        {
          field: 'Valid',
          flex: 1,
          headerName: 'Valid config',
          renderCell: ({ row }) => {
            if (row.validConfig === null)
              return <Typography>Validating...</Typography>
            if (row.validConfig === false)
              return <Typography color="error">Invalid</Typography>
            return <Typography color="success">Valid</Typography>
          },
        },
        hideColumns?.valid
      ),
      ...(extraColumns ?? []),
    ])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraColumns, hideColumns])

  if (!data) return null

  return (
    <>
      <When condition={data.length > 0}>
        <Box flex={1} width="100%">
          <DataGrid
            autoPageSize
            columns={columns}
            getRowId={prop('id')}
            rows={data}
          />
        </Box>
      </When>
      <Unless condition={data.length > 0}>
        <Box
          alignItems="center"
          display="flex"
          flex={1}
          flexDirection="column"
          gap={1}
          justifyContent="center"
        >
          <Typography variant="h6">No published versions</Typography>
          <Typography variant="caption">You can create one here</Typography>
          {publishDialog}
        </Box>
      </Unless>
    </>
  )
}

export default HistoricList
